import {
  BarcodeOutlined,
  DeleteOutlined,
  PlusCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Table,
} from "antd";
import Search from "antd/es/input/Search";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import DatePicker from "../../components/DatePicker/DatePicker";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import { CrudAction } from "../../enums/crudAction.enum";
import styles from "../../index.style";
import { sharedInternalInfoState } from "../../recoil/atoms/sharedInternalAtom";
import { userInfoState } from "../../recoil/atoms/userInfoAtom";
import ImplantablesService from "../../services/implantables.service";
import { Implantable } from "../../types/implantable.types";
import { decodeGS1, validateGS1 } from "../../utils/gsbarcode.utils";

// definisco un type perchè altrimenti non si capisce quali sono i campi
type ImplantableRecord = {
  implantable: {
    uuid: string;
    label: string;
  };
  lot: string;
  expirationDate: DateTime | string;
  quantity: number;
  serial: string;
};

// extend type ImplantableRecord with custom fields
type ImplantableRecordEdit = ImplantableRecord & {
  expirationDate: string;
  implantableInfo: {
    uuid: string;
    code: string;
    description: string;
  };
};

function Implantables() {
  const { t, i18n } = useTranslation();
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchForm] = Form.useForm();
  const [sharedInfo] = useRecoilState(sharedInternalInfoState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [implantableList, setImplantableList] = useState<Implantable[]>([]);
  const [paginationLoadList, setPaginationLoadList] = useState(50);
  const [implantableType, setImplantableType] = useState(); //uuid dell'articolo selezionato
  const [modifyType, setModifyType] = useState(false);


  const [currentStatus, setCurrentStatus] = useState("");

  const isDisabled = currentStatus === "validated";

  const [mode, setMode] = useState<CrudAction>(CrudAction.ADD);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [form] = Form.useForm();
  const [matrixArticles, setMatrixArticles] = useState<{
    implantables: string;
  }>({ implantables: "" });

  const labelColSpan = 16;
  const wrapperColSpan = 8;

  const columns = [
    {
      title: " ",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: ImplantableRecordEdit, rowIndex: number) => (
        <div>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            style={{ backgroundColor: styles.blueColor, padding: "7px" }}
            className="customButtonNoFloat"
            onClick={() => edit(record, rowIndex)}
          />
        </div>
      ),
    },
    {
      title: `${t("IMPLANTABLE")}`,
      dataIndex: "implantable",
      key: "implantables",
      render: (text: any, record: any) => (
        <div>
          <div>
            {record.implantableInfo.code} - {record.implantableInfo.description}{" "}
            ({record.implantableInfo.measureUnit})
          </div>
        </div>
      ),
    },
    {
      title: `${t("QUANTITY")}`,
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: `${t("EXPIRATION")}`,
      dataIndex: "expirationDate",
      key: "expirationDate",
      render: (date: string) =>
        date
          ? DateTime.fromISO(date, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toLocaleString(DateTime.DATE_SHORT)
          : null,
    },
    {
      title: `${t("LOT")}`,
      dataIndex: "lot",
      key: "lot",
    },
    {
      title: `${t("SERIAL")}`,
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: " ",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: any, rowIndex: number) => (
        <div>
          <Button
            type="primary"
            icon={<DeleteOutlined />}
            style={{ backgroundColor: styles.red, padding: "7px" }}
            className="customButtonNoFloat"
            onClick={() => deleteImplantable(rowIndex)}
            disabled={isDisabled}
          />
        </div>
      ),
    },
  ];


  const loadData = () => {
    const payload = {
      uuid: sharedInfo.surgery,
      tabName: "implantables-tab",
    };

    changeShowLoading(
      ImplantablesService.loadData(payload)
        .then((res) => {
          setTableData(res.implantable);
          setCurrentStatus(res?.currentStatus || "");
        })
        .catch((err) => {
          console.error(err);
        })
    );
  };

  const loadImplantablesList = (filter: string) => {
    const payload = [
      {
        field: "article_type.code",
        operator: "equal",
        value: matrixArticles.implantables,
      },
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
    ];

    const queryparams = {
      page: 1,
      take: filter === "" ? paginationLoadList : 10000000000000,
      searchOn: "code,description",
      searchFor: filter,
    };

    if (matrixArticles.implantables) {
      changeShowLoading(
        ImplantablesService.getImplantables(payload, queryparams)
          .then((res) => {
            setImplantableList(res.list);
          })
          .catch((err) => {
            console.error(t("error"), err);
          })
      );
    }
  };

  const loadMatrixValue = () => {
    let matrix = userInfo.matrixPar["system.articles"];
    try {
      let info = JSON.parse(matrix);

      info = {
        implantables: info.implantables,
      };

      setMatrixArticles(info);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    loadMatrixValue();
  }, []);

  React.useEffect(() => {
    loadData();
  }, [matrixArticles]);

  const addImplantable = () => {
    setIsModalOpen(true);
    setMode(CrudAction.ADD);
  };

  const handleOk = async () => {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_IMPLANTABLES_TAB"] ||
      true
    ) {
      // const valid = await form.validateFields().catch((err) => {
      //   console.error(err);
      //   return false;
      // });

      const values = form.getFieldsValue();

      if (!values.implantable || !values.lot || !values.expirationDate) {
        message.error(t("REQUIRED_FIELDS"));
        return;
      }

      const data = form.getFieldsValue();
      const implantables = tableData.map((item) => {
        return {
          lot: item.lot,
          expirationDate: item.expirationDate,
          quantity: item.quantity || 1,
          implantable: item.implantableInfo.uuid,
          serial: item.serial,
        };
      });

      if (mode === CrudAction.ADD) {
        implantables.push(data);
      } else if (mode === CrudAction.UPDATE) {
        const currentIndex =
          (pagination.page - 1) * pagination.pageSize + selectedRowIndex;
        implantables[currentIndex] = data;
      }

      updateImplantables(implantables);
    } else {
      message.error(t("PERMISSION_NEGATE"));
      setIsModalOpen(false);
      setModifyType(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setIsModalOpen(false);
    setModifyType(false);
    setImplantableType(undefined);
  };

  const onDrugTypeChange = (value: any) => {
    setImplantableType(value);
  };

  const edit = (record: ImplantableRecordEdit, rowIndex: number) => {
    setMode(CrudAction.UPDATE);
    setModifyType(true);
    setIsModalOpen(true);
    setSelectedRowIndex(rowIndex);

    form.setFieldsValue({
      implantable: {
        uuid: record.implantableInfo.uuid,
        label:
          record.implantableInfo.code +
          " - " +
          record.implantableInfo.description,
      },
      lot: record.lot,
      expirationDate: DateTime.fromISO(record.expirationDate, {
        zone: "Europe/Rome", //TODO: Da scodare
      }),
      quantity: record.quantity,
      serial: record.serial,
    });
  };

  const onScroll = (value: any) => {
    const target = value.target;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      setPaginationLoadList(paginationLoadList + 50);
    }
  };

  React.useEffect(() => {
    loadImplantablesList("");
  }, [paginationLoadList]);

  const onSearchValue = (value: any) => {
    loadImplantablesList(value);
  };

  const deleteImplantable = (rowIndex: number) => {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_IMPLANTABLES_TAB"]
    ) {
      let implantables = tableData.map((item) => {
        return {
          lot: item.lot,
          expirationDate: item.expirationDate,
          quantity: item.quantity,
          implantable: item.implantableInfo.uuid,
        };
      });

      const currentIndex =
        (pagination.page - 1) * pagination.pageSize + rowIndex;
      implantables.splice(currentIndex, 1);
      updateImplantables(implantables);
    } else {
      message.error(t("PERMISSION_NEGATE"));
    }
  };

  const updateImplantables = (implantables: Implantable[]) => {
    changeShowLoading(
      ImplantablesService.update(sharedInfo.surgery, {
        tabName: "implantables-tab",
        content: implantables,
      })
        .then(() => {
          setIsModalOpen(false);
          setModifyType(false);
          loadData();
        })
        .catch((err) => {
          console.error(err);
        })
    );
  };

  const resetForm = () => {
    form.resetFields();
  };

  React.useEffect(() => {
    if (isModalOpen) loadImplantablesList("");
    if (!isModalOpen) resetForm();
  }, [isModalOpen]);

  const searchAndDecodeBarcode = async (value: string) => {
    let barcode;
    let record = {} as ImplantableRecord;

    if (!value) {
      console.warn("Barcode vuoto");
      return;
    }

    searchForm.setFieldValue("search", "");

    // se ho un UDI-DI/PI allora passa la validazione altrimenti barcode standard
    if (validateGS1(value)) {
      console.log("UDI-DI/PI", value);

      let decodedGS1 = decodeGS1(value);
      console.log("UDI-DI/PI Decoded", decodedGS1);
      let expirationDate = decodedGS1.expiryDate || "";

      barcode = decodedGS1.GTIN;
      record.expirationDate = DateTime.fromFormat(expirationDate, "yyMMdd");
      record.lot = decodedGS1.batchNumber ?? "";
      record.serial = decodedGS1.serialNumber ?? "";
    } else {
      // è un barcode standard
      barcode = value;
    }

    const payload = [
      {
        field: "article_type.code",
        operator: "equal",
        value: matrixArticles.implantables,
      },
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
      {
        field: "t.barcode",
        operator: "equal",
        value: barcode,
      },
    ];

    const queryparams = {
      page: 1,
      take: 10000000000000,
      searchOn: "code,description",
      searchFor: "",
    };

    try {
      const [article] = await ImplantablesService.getImplantables(
        payload,
        queryparams
      ).then((res) => res.list);

      if (!article) {
        message.error(t("ARTICLE_NOT_FOUND"));
        return;
      }

      setImplantableType(article.uuid);
      record.implantable = {
        uuid: article.uuid,
        label: article.code + " - " + article.description,
      };
    } catch (err) {
      console.error(err);
    }

    record.quantity = 1;

    addImplantable();

    console.log({ record });
    form.setFieldsValue({ ...record });
  };

  return (
    <>
      <div className="page-container-margin">
        <div
          style={{ borderBottom: "1px solid #EEE" }}
          className="flex w-full justify-between items-center border-opacity-30 pb-4 text-left"
        >
          <div className="flex items-left">
            <Form form={searchForm} layout="inline">
              <Form.Item name="search" className="form-container-item">
                <Input
                  id="search-and-decode-barcode"
                  addonBefore={<BarcodeOutlined className="text-teal-700" />}
                  addonAfter={<SearchOutlined className="text-teal-700" />}
                  placeholder={`${t("SEARCH")}`}
                  onKeyUp={(e: any) => {
                    if (e.key === "Enter") {
                      searchAndDecodeBarcode(e.target.value);
                    }
                  }}
                  style={{ width: 304 }}
                  allowClear
                />
              </Form.Item>
            </Form>
          </div>

          <div className="w-full flex justify-end">
            <Button
              type="primary"
              icon={<PlusCircleFilled />}
              className="customButton"
              style={{ backgroundColor: styles.buttonTeal }}
              onClick={addImplantable}
              disabled={isDisabled}
            >
              {t("ADD_IMPLANTABLE")}
            </Button>
          </div>
        </div>
        <Modal
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          closable={false}
          maskClosable={false}
          okButtonProps={{
            disabled: implantableType || modifyType ? false : true,
          }}
        >
          <div className="form-container">
            <Form
              name="add_implantable"
              className="sisos-form"
              form={form}
              layout="horizontal"
              labelCol={{
                span: 8,
              }}
              wrapperCol={{
                span: 20,
              }}
              disabled={isDisabled}
            >
              <Form.Item
                name="implantable"
                className="form-container-item"
                label={t("TYPOLOGY")}
                rules={[
                  {
                    required: true,
                    message: `${t("REQUIRED_FIELD")}`,
                    type: "string",
                  },
                ]}
              >
                <Select
                  showSearch
                  className="w-full"
                  placeholder={t("TYPOLOGY")}
                  optionFilterProp="children"
                  allowClear
                  fieldNames={{ label: "description", value: "uuid" }}
                  onChange={onDrugTypeChange}
                  onPopupScroll={onScroll}
                  onSearch={onSearchValue}
                  filterOption={(input, option: any) =>
                    option.description
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  options={implantableList}
                />
              </Form.Item>

              <Form.Item
                name="lot"
                className="form-container-item"
                label={t("LOT")}
                rules={[
                  {
                    required: true,
                    message: `${t("REQUIRED_FIELD")}`,
                    type: "string",
                  },
                ]}
              >
                <Input placeholder={`${t("BASIC_USAGE")}`} />
              </Form.Item>

              <Form.Item
                name="expirationDate"
                className="form-container-item"
                label={t("EXPIRATION_DATE")}
                rules={[
                  {
                    required: true,
                    message: `${t("REQUIRED_FIELD")}`,
                    type: "object",
                  },
                ]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item
                name="serial"
                className="form-container-item"
                label={t("SERIAL")}
              >
                <Input placeholder={`${t("SERIAL")}`} />
              </Form.Item>

              <Form.Item
                name="quantity"
                label={t("QUANTITY")}
                className="form-container-item"
                initialValue={1}
              >
                <InputNumber size="large" type="number" stringMode min="1" />
              </Form.Item>
            </Form>
          </div>
        </Modal>
        <div className="sisos-table-container">
          <Table
            dataSource={tableData}
            size="small"
            className="sisos-table"
            columns={columns}
            pagination={{
              position: ["topCenter"],
              size: "small",
            }}
            onChange={(pagination, filters, sorter, extra) => {
              setPagination({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
              });
            }}
          />
        </div>
      </div>
    </>
  );
}

export default Implantables;
